'use client';

import { useEffect, useState } from 'react';

const PROD_URL = 'https://www.omceanbooking.com';
const DEV_DOMAIN = 'www1.omceanbooking.com';
const ADMIN_STORAGE_KEY = 'admin_access';
const REDIRECT_DELAY = 5; // seconds

/**
 * Dev Access Guard
 *
 * Client-side access control for the dev environment (www1.omceanbooking.com).
 * - If ?admin is in URL → grants access and stores in localStorage
 * - If admin_access exists in localStorage → allows access
 * - Otherwise → shows redirect message and redirects to production
 *
 * Only runs on www1.omceanbooking.com - does nothing on production or localhost.
 */
export default function DevAccessGuard() {
  const [showRedirect, setShowRedirect] = useState(false);
  const [countdown, setCountdown] = useState(REDIRECT_DELAY);

  useEffect(() => {
    // Only run on dev domain
    if (typeof window === 'undefined') return;
    if (!window.location.hostname.includes(DEV_DOMAIN)) return;

    // Check for ?admin param
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('admin')) {
      localStorage.setItem(ADMIN_STORAGE_KEY, 'true');
      // Remove ?admin from URL without reload
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('admin');
      window.history.replaceState({}, '', newUrl.toString());
      return;
    }

    // Check localStorage
    if (localStorage.getItem(ADMIN_STORAGE_KEY) === 'true') {
      return;
    }

    // No access - show redirect message
    setShowRedirect(true);
  }, []);

  // Countdown and redirect
  useEffect(() => {
    if (!showRedirect) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = PROD_URL;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showRedirect]);

  if (!showRedirect) return null;

  // Full-screen redirect overlay
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#333',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxWidth: '500px',
        }}
      >
        <div
          style={{
            width: '60px',
            height: '60px',
            margin: '0 auto 20px',
            borderRadius: '50%',
            backgroundColor: '#3880ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </div>

        <h1 style={{ margin: '0 0 16px', fontSize: '24px', fontWeight: 600 }}>
          Taking You to Production
        </h1>

        <p
          style={{
            margin: '0 0 24px',
            fontSize: '16px',
            color: '#666',
            lineHeight: 1.5,
          }}
        >
          Taking you to our production website.
          <br />
          It&apos;s faster, smarter, and better looking.
        </p>

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            backgroundColor: '#f0f0f0',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#666',
          }}
        >
          <span>Redirecting in</span>
          <span
            style={{
              fontWeight: 700,
              fontSize: '18px',
              color: '#3880ff',
              minWidth: '20px',
            }}
          >
            {countdown}
          </span>
          <span>seconds...</span>
        </div>

        <div style={{ marginTop: '24px' }}>
          <a
            href={PROD_URL}
            style={{
              color: '#3880ff',
              textDecoration: 'none',
              fontSize: '14px',
            }}
          >
            Click here if not redirected automatically
          </a>
        </div>
      </div>
    </div>
  );
}
